<template>
    <span v-if="shouldWrap" :class="wrapperClass" v-floating-tooltip="tippy" tabindex="0">
        <router-link v-if="to" v-slot="{ href: routeHref, navigate }" :to="to" custom>
            <a v-bind="linkProps(routeHref)" @click="(event) => handleRouterClick(event, navigate)">
                <fa class="text-lg" v-if="busy" :icon="['fas', 'spinner']" spin fixed-width :class="spinnerClass" />
                <span :class="contentClass">
                    <slot>
                        <t v-if="label" :path="label" />
                    </slot>
                </span>
            </a>
        </router-link>

        <a v-else-if="href" v-bind="anchorProps" @click="handleClick">
            <fa class="text-lg" v-if="busy" :icon="['fas', 'spinner']" spin fixed-width :class="spinnerClass" />
            <span :class="contentClass">
                <slot>
                    <t v-if="label" :path="label" />
                </slot>
            </span>
        </a>

        <button v-else v-bind="buttonProps" :type="type" @click="handleClick">
            <fa class="text-lg" v-if="busy" :icon="['fas', 'spinner']" spin fixed-width :class="spinnerClass" />
            <span :class="contentClass">
                <slot>
                    <t v-if="label" :path="label" />
                </slot>
            </span>
        </button>
    </span>

    <router-link v-else-if="to" v-slot="{ href: routeHref, navigate }" :to="to" custom>
        <a v-bind="linkProps(routeHref)" @click="(event) => handleRouterClick(event, navigate)">
            <fa class="text-lg" v-if="busy" :icon="['fas', 'spinner']" spin fixed-width :class="spinnerClass" />
            <span :class="contentClass">
                <slot>
                    <t v-if="label" :path="label" />
                </slot>
            </span>
        </a>
    </router-link>

    <a v-else-if="href" v-bind="anchorProps" @click="handleClick">
        <fa class="text-lg" v-if="busy" :icon="['fas', 'spinner']" spin fixed-width :class="spinnerClass" />
        <span :class="contentClass">
            <slot>
                <t v-if="label" :path="label" />
            </slot>
        </span>
    </a>

    <button v-else v-bind="buttonProps" :type="type" @click="handleClick">
        <fa class="text-lg" v-if="busy" :icon="['fas', 'spinner']" spin fixed-width :class="spinnerClass" />
        <span :class="contentClass">
            <slot>
                <t v-if="label" :path="label" />
            </slot>
        </span>
    </button>
</template>

<script setup lang="ts">
import type { ClassValue } from 'clsx';
import type { HTMLAttributes, Ref } from 'vue';
import { computed, inject, onBeforeMount, onBeforeUnmount, ref, useAttrs } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { cn } from '~/lib/utils';
import { translateRequiresPermissions, usePermissionsStore } from '~/plugins/permission';

defineOptions({
    inheritAttrs: false,
});

type ButtonColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'danger';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type ButtonType = 'button' | 'submit' | 'reset';

interface Props {
    color?: ButtonColor;
    size?: ButtonSize;
    permission?: string | string[];
    /** Array permission matches when the user holds ANY entry (default: all). */
    permissionAny?: boolean;
    tooltip?: string;
    tooltipPlacement?: string;
    href?: string;
    to?: RouteLocationRaw;
    type?: ButtonType;
    label?: string;
    spanClass?: HTMLAttributes['class'];
    spinner?: boolean;
    showSpinner?: boolean;
    loading?: boolean;
    outline?: boolean;
    /** Emphasis fill — see buttonSolidClasses. Mutually exclusive with `outline`. */
    solid?: boolean;
    full?: boolean;
    square?: boolean;
    tile?: boolean;
    flushTop?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    /* md = the 32px compact control rhythm (matches .input) */
    size: 'md',
    tooltipPlacement: 'bottom',
    type: 'button',
});

const attrs = useAttrs();
const permissions = usePermissionsStore();
const clickShowSpinner = ref(false);
const formSubmitting = ref(false);
const formCanSubmit = inject<Ref<boolean> | null>('formCanSubmit', null);
const registerFormComponent = inject<registerFormComponentFn | null>('registerFormComponent', null);

let unregisterFormButton: (() => void) | null = null;

onBeforeMount(() => {
    if (props.type !== 'submit') {
        return;
    }

    unregisterFormButton =
        registerFormComponent?.({
            onSubmit: (state) => (formSubmitting.value = state),
        }) ?? null;
});

onBeforeUnmount(() => {
    unregisterFormButton?.();
    unregisterFormButton = null;
});

const hasPerms = computed(() => (props.permission ? permissions.has(props.permission, props.permissionAny === true) : true));
const busy = computed(() => props.loading || props.showSpinner || clickShowSpinner.value || formSubmitting.value);
const formSubmitDisabled = computed(() => props.type === 'submit' && formCanSubmit?.value === false);

const disabled = computed(() => {
    if (attrs.disabled || attrs.disabled === '') {
        return true;
    }

    return formSubmitDisabled.value || !hasPerms.value || busy.value;
});

const hasTooltip = computed(() => props.tooltip || (props.permission && !hasPerms.value));
const shouldWrap = computed(() => disabled.value || hasTooltip.value);

const semanticColorClasses = computed(() => {
    if (!props.color) {
        return [];
    }

    const colorMap: Record<ButtonColor, string> = {
        primary: 'btn-primary',
        secondary: 'btn-secondary',
        info: 'btn-info',
        success: 'btn-success',
        warning: 'btn-warning',
        danger: 'btn-danger',
    };

    return ['btn', colorMap[props.color]];
});

const semanticSizeClasses = computed(() => (props.color ? [`btn-${props.size}`] : []));

const buttonBaseClasses =
    'inline-flex min-w-min items-center justify-center rounded border-2 border-solid border-transparent bg-primary-500 px-3 py-1 text-center text-sm shadow-none transition-colors hover:cursor-pointer focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:outline-none';

const buttonColorClasses: Record<ButtonColor, string> = {
    primary: 'text-accent-500 hover:text-accent-300',
    secondary: 'text-primary-200 hover:text-primary-50',
    info: 'text-info-500 hover:text-info-300',
    success: 'text-success-500 hover:text-success-300',
    warning: 'text-warning-500 hover:text-warning-300',
    danger: 'text-danger-500 hover:text-danger-300',
};

/* Emphasis fill. The default button is one flat weight — every colour is
   bg-primary-500 with semantic *text* — so a screen of them has no primary
   action, just six shades of the same grey chip. `solid` is the opt-in second
   tier: the semantic colour becomes the surface, so the one action a page
   exists for can outrank everything beside it.

   Foregrounds are measured, not matched by number. The bright semantics take
   dark text on the -500 fill (accent 8.6:1, success 7.2:1, info 9.7:1,
   warning 9.1:1); a light foreground on those same fills lands at 1.7–2.3:1
   and is illegible. Danger inverts because it is a dark red rather than a
   bright one: light text on danger-600 gives 5.9:1, where light on danger-500
   is 4.47:1 and misses AA.

   Hover moves the surface and not the text, per the one-hover-channel rule —
   with the foreground pinned by contrast there is nothing else to move. */
const buttonSolidClasses: Record<ButtonColor, string> = {
    primary: 'bg-accent-500 text-primary-950 hover:bg-accent-400',
    secondary: 'bg-primary-300 text-primary-950 hover:bg-primary-200',
    info: 'bg-info-500 text-primary-950 hover:bg-info-400',
    success: 'bg-success-500 text-primary-950 hover:bg-success-400',
    warning: 'bg-warning-500 text-primary-950 hover:bg-warning-400',
    danger: 'bg-danger-600 text-danger-50 hover:bg-danger-500',
};

/* Compact control rhythm: md = 32px (matches .input), lg = 36px, xl = 40px.
   The height is stated rather than derived from padding + line-height, because a
   button whose only child is an icon takes the SVG's height (~16px) instead of the
   text line-height (20px) — which made icon buttons 4px shorter than text buttons
   and than the inputs beside them. min-h keeps multi-line content from clipping;
   the 2px transparent border is inside the height (border-box). */
const buttonSizeClasses: Record<ButtonSize, string> = {
    xs: 'min-h-7 px-1.5 py-1 text-xs',
    sm: 'min-h-7 px-2 py-1 text-xs',
    md: 'min-h-8 px-3 py-1 text-sm',
    lg: 'min-h-9 px-3 py-1.5 text-sm',
    xl: 'min-h-10 px-4 py-2 text-sm',
};

const buttonUtilityClasses = computed(() => {
    if (!props.color) {
        return [];
    }

    return [
        buttonBaseClasses,
        props.solid ? buttonSolidClasses[props.color] : buttonColorClasses[props.color],
        buttonSizeClasses[props.size],
        /* Last wins through twMerge, so outline still strips a solid fill back
           to a hairline if both are somehow passed. */
        props.outline ? 'border-current bg-transparent' : null,
    ];
});

const forwardedAttrs = computed(() => {
    const { class: _class, disabled: _disabled, onClick: _onClick, ...rest } = attrs;

    return rest;
});

const wrapperClass = computed(() => cn('contents focus:outline-none', props.full ? 'block w-full' : null, props.spanClass as ClassValue));
const contentClass = computed(() => cn(props.color ? 'inline-flex items-center justify-center gap-2' : 'contents', busy.value ? 'invisible' : null));
const spinnerClass = 'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2';

const baseProps = computed(() => ({
    ...forwardedAttrs.value,
    class: cn(
        'relative min-h-8 cursor-pointer',
        semanticColorClasses.value,
        semanticSizeClasses.value,
        props.outline ? 'btn-outline' : null,
        /* Marker only — the styling lives in buttonUtilityClasses. External
           themes hook these class names, so a new variant has to expose one. */
        props.solid ? 'btn-solid' : null,
        attrs.class as ClassValue,
        buttonUtilityClasses.value,
        /* Square icon buttons sit on the same rhythm as their size, not a step above it. */
        props.square ? 'h-8 w-8 justify-center p-0' : null,
        props.tile ? 'h-11 w-full justify-center rounded md:aspect-square md:h-auto' : null,
        props.flushTop ? 'rounded-t-none' : null,
        props.full ? 'w-full' : null,
        disabled.value ? 'disabled pointer-events-none cursor-not-allowed opacity-40' : null,
    ),
    'aria-disabled': disabled.value || undefined,
}));

const buttonProps = computed(() => ({
    ...baseProps.value,
    type: props.type,
    disabled: disabled.value,
}));

const anchorProps = computed(() => ({
    ...baseProps.value,
    href: disabled.value ? undefined : props.href,
}));

const linkProps = (href: string): Record<string, unknown> => {
    return {
        ...baseProps.value,
        href: disabled.value ? undefined : href,
    };
};

const tippy = computed(() => {
    if (props.permission && !hasPerms.value) {
        return [translateRequiresPermissions(props.permission), props.tooltipPlacement, true];
    }

    return props.tooltip ? [props.tooltip, props.tooltipPlacement] : null;
});

const callClickHandler = async (evt: MouseEvent): Promise<void> => {
    const clickHandler = attrs.onClick;

    if (Array.isArray(clickHandler)) {
        for (const handler of clickHandler) {
            if (typeof handler === 'function') {
                await handler(evt);
            }
        }

        return;
    }

    if (typeof clickHandler === 'function') {
        await clickHandler(evt);
    }
};

const runClickHandler = async (evt: MouseEvent): Promise<void> => {
    if (!attrs.onClick) {
        return;
    }

    evt.stopImmediatePropagation();

    if (props.spinner) {
        clickShowSpinner.value = true;
    }

    await callClickHandler(evt);

    if (props.spinner) {
        clickShowSpinner.value = false;
    }
};

const handleClick = async (evt: MouseEvent): Promise<void> => {
    if (disabled.value) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        return;
    }

    await runClickHandler(evt);
};

const handleRouterClick = async (evt: MouseEvent, navigate: (event?: MouseEvent) => void | Promise<unknown>): Promise<void> => {
    if (disabled.value) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        return;
    }

    await runClickHandler(evt);

    if (!evt.defaultPrevented) {
        await navigate(evt);
    }
};
</script>
